import React, { PropsWithChildren, ReactNode, useContext } from 'react';
import { Box, Container, Link, Stack, Typography } from '@mui/material';
import { useSelector } from 'react-redux';
import { Link as RouterLink } from 'react-router-dom';
import { AuthContext } from 'src/contexts/AuthContext';
import { getApplicationSettings } from 'src/modules/applications/slices';
import { SignupFooter, SignupHeader } from './SignupLayout.styles';

type Props = {
  /**
   * Set by a screen that scrolls a list of its own. The main area then keeps no bottom gap, so the list ends
   * at the footer instead of a strip of empty page that looks like the list simply stopped early.
   */
  scrollsInside?: boolean;
  /**
   * A way off this screen, shown at the right of the footer in place of the login prompt. The two never meet:
   * the prompt is for visitors without an account, and a screen with somewhere of its own to send the client
   * is past that.
   */
  footerAction?: ReactNode;
};

/**
 * Chrome shared by every funnel screen. Branding comes from the store, which the unauthenticated
 * `AuthProvider` fills by domain, so the layout needs no request of its own.
 */
export const SignupLayout: React.FC<PropsWithChildren<Props>> = ({
  scrollsInside = false,
  footerAction,
  children,
}) => {
  const settings = useSelector(getApplicationSettings);
  const authContext = useContext(AuthContext);

  /**
   * The funnel spans both sides of the login: the plan and company screens are for visitors, the number and
   * payment screens run on the account that was just created. Offering to log in on those last two invites a
   * client to go and find credentials for the session they are already in.
   *
   * Only an explicit `false` shows it. While the provider is still working out who this is the answer is
   * `null`, and treating that as a visitor would flash the prompt at everyone on the way in.
   */
  const invitedToLogIn = authContext?.isAuthenticated === false;
  const showLoginPrompt = footerAction == null && invitedToLogIn;
  const footerItems = [showLoginPrompt, Boolean(settings?.email), footerAction != null].filter(Boolean).length;

  return (
    /*
      A fixed height rather than a minimum, so the scroll belongs to the middle and the branding and the help
      links stay where the client can see them. `dvh` where the browser has it: on a phone `100vh` counts the
      address bar that is not there, and the footer ends up just below the fold.
    */
    <Box
      display="flex"
      flexDirection="column"
      sx={{ height: '100vh', '@supports (height: 100dvh)': { height: '100dvh' } }}
    >
      <SignupHeader>
        <Container maxWidth="md" sx={{ display: 'flex', alignItems: 'center', minHeight: 80 }}>
          {settings?.logo ? (
            <Box
              component="img"
              src={settings.logo}
              alt={settings.name}
              sx={{ width: 165, minHeight: 25, objectFit: 'contain' }}
            />
          ) : (
            <Typography variant="h6" fontWeight="fontWeightSemiBold">
              {settings?.name}
            </Typography>
          )}
        </Container>
      </SignupHeader>

      {/*
        `minHeight: 0` is what lets this shrink below its content instead of pushing the footer out of view,
        and the column is here so a screen with a long list of its own can hand the scrolling to that list.
      */}
      <Container
        component="main"
        maxWidth="md"
        sx={{
          flex: 1,
          minHeight: 0,
          pt: 6,
          pb: scrollsInside ? 0 : 6,
          display: 'flex',
          flexDirection: 'column',
          // Kept even for a screen that scrolls inside: on a viewport too short for the filters above the
          // list, this is the only way down to them.
          overflowY: 'auto',
        }}
      >
        {children}
      </Container>

      <SignupFooter>
        <Container maxWidth="md" sx={{ py: 3 }}>
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            spacing={1}
            alignItems={{ sm: 'center' }}
            // Only the login prompt belongs on the left. Anything else on its own stays on the right, where
            // `space-between` would not keep it.
            justifyContent={showLoginPrompt || footerItems > 1 ? 'space-between' : 'flex-end'}
          >
            {showLoginPrompt && (
              <Typography variant="body2" color="text.secondary">
                Already have an account?{' '}
                <Link component={RouterLink} to="/login">
                  Log in
                </Link>
              </Typography>
            )}

            {settings?.email && (
              <Typography variant="body2" color="text.secondary">
                Need help? <Link href={`mailto:${settings.email}`}>{settings.email}</Link>
              </Typography>
            )}

            {footerAction}
          </Stack>
        </Container>
      </SignupFooter>
    </Box>
  );
};
